import React, { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { FormResponseDto } from "../../Models/FormResponseDto";
import FormApi from "../../Api/FormApi";
import { useParams } from "react-router-dom";
import { Button, Modal } from "flowbite-react";
import EditFormResponsePage from "./EditFormResponsePage";
import { toast } from "react-toastify";

const FormSubmissionsPage: React.FC = () => {
  const { id } = useParams();
  const [responses, setResponses] = useState<FormResponseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [formInfo, setFormInfo] = useState<any>(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [externalSubmitFn, setExternalSubmitFn] = useState<(() => void) | null>(
    null
  );
  const [selectedResponseId, setSelectedResponseId] = useState<number | null>(
    null
  );

  // Search
  const [searchText, setSearchText] = useState("");

  // Delete confirmation
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [downloadingReportId, setDownloadingReportId] = useState<number | null>(
    null
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const [responsesRes, formRes] = await Promise.all([
        FormApi.getResponses(Number(id)),
        FormApi.getForm(Number(id)),
      ]);

      setResponses(responsesRes.data ?? []);
      setFormInfo(formRes.data ?? null);

      // Collect unique field labels for CSV headers (kept from your original)
      const fieldSet = new Set<string>();
      (responsesRes.data ?? []).forEach((resp: FormResponseDto) => {
        resp.answers?.forEach((a) => fieldSet.add(a.question));
      });
      setCsvHeaders([...fieldSet]);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load form submissions.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const makeFileNameSafe = (name: string) =>
    name
      .trim()
      .replace(/[/\\?%*:|"<>]/g, "-")
      .replace(/\s+/g, "_")
      .substring(0, 255);

  const filteredResponses = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return responses;

    const contains = (s?: string | number | null) =>
      (s ?? "").toString().toLowerCase().includes(q);

    return responses.filter((r) => {
      const answersText =
        r.answers
          ?.map(
            (a) =>
              `${a.question ?? ""} ${a.answer ?? ""} ${
                a.price != null ? a.price.toFixed(2) : ""
              }`
          )
          .join(" ") ?? "";

      return (
        contains(r.memberName) ||
        contains(r.submittedByFirstName) ||
        contains(r.submittedByLastName) ||
        contains(r.submittedByEmail) ||
        contains(r.submittedByPhone) ||
        contains(r.filledByName || "") ||
        contains(r.status ?? "Pending") ||
        contains(new Date(r.submittedDate).toLocaleString()) ||
        contains(r.totalPrice != null ? r.totalPrice.toFixed(2) : "") ||
        contains(answersText)
      );
    });
  }, [responses, searchText]);

  // CSV should export "what you see"
  const csvData = useMemo(() => {
    return filteredResponses.map((r) => {
      const flat: any = {
        responseId: r.responseId,
        companyName: r.memberName,
        firstName: r.submittedByFirstName,
        lastName: r.submittedByLastName,
        email: r.submittedByEmail,
        phone: r.submittedByPhone,
        submittedDate: new Date(r.submittedDate).toLocaleString(),
        status: r.status ?? "Pending",
        totalPrice: r.totalPrice?.toFixed(2) ?? "",
      };
      r.answers?.forEach((a) => {
        let answerText =
          a.answer + (a.price ? ` ($${a.price.toFixed(2)})` : "");

        // Add field-level users
        if (a.users && a.users.length > 0) {
          answerText +=
            "; Participants: " +
            a.users
              .map(
                (u, i) =>
                  `${i + 1}. ${u.firstName} ${u.lastName}${
                    u.userType ? ` - ${u.userType}` : ""
                  }${u.price ? ` ($${u.price.toFixed(2)})` : ""}`
              )
              .join(", ");
        }

        // Add option-level users
        if (a.optionUsers && Object.keys(a.optionUsers).length > 0) {
          Object.entries(a.optionUsers).forEach(([optionValue, users]) => {
            answerText +=
              `; ${optionValue} Participants: ` +
              users
                .map(
                  (u, i) =>
                    `${i + 1}. ${u.firstName} ${u.lastName}${
                      u.userType ? ` - ${u.userType}` : ""
                    }${u.price ? ` ($${u.price.toFixed(2)})` : ""}`
                )
                .join(", ");
          });
        }

        flat[a.question] = answerText;
      });
      return flat;
    });
  }, [filteredResponses]);

  // Download report
  const handleDownloadReport = async (responseId: number) => {
    try {
      setDownloadingReportId(responseId);
      const response = await FormApi.downloadResponseReport(responseId);

      // Create a blob from the response data
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.download = `FormResponse_${responseId}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Report downloaded successfully");
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error("Failed to download report");
    } finally {
      setDownloadingReportId(null);
    }
  };

  // Delete flow
  const requestDelete = (id: number) => setConfirmDeleteId(id);

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      setDeleting(true);
      await FormApi.deleteResponse(confirmDeleteId);
      setResponses((prev) =>
        prev.filter((x) => x.responseId !== confirmDeleteId)
      );
      toast.success("Submission deleted.");
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete submission.");
    } finally {
      setDeleting(false);
      setConfirmDeleteId(null);
    }
  };

  if (loading) return <div className="p-6 w-full">Loading...</div>;

  if (responses.length === 0)
    return (
      <div className="p-6 w-full">
        {/* Form Title & Description */}
        {formInfo && (
          <div className="mb-6 border border-gray-300 bg-gray-50 p-4 rounded">
            <h1 className="text-2xl font-bold mb-1">{formInfo.title}</h1>
            {formInfo.description && (
              <p className="text-gray-700">{formInfo.description}</p>
            )}
          </div>
        )}
        <div className="border border-gray-300 bg-white rounded-lg shadow-md p-6 mb-6 w-full">
          No responses found.
        </div>
      </div>
    );

  return (
    <div className="w-full p-6 overflow-x-auto">
      {/* Form Title & Description */}
      {formInfo && (
        <div className="w-full max-w-full mb-6 border border-gray-300 bg-gray-50 p-4 rounded">
          <h1 className="text-2xl font-bold mb-1">{formInfo.title}</h1>
          {formInfo.description && (
            <p className="text-gray-700">{formInfo.description}</p>
          )}
        </div>
      )}

      {/* Header: Search + CSV */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center mb-4">
        <h3 className="text-2xl font-bold">
          Form Submissions{" "}
          {searchText && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({filteredResponses.length} match
              {filteredResponses.length === 1 ? "" : "es"})
            </span>
          )}
        </h3>

        <div className="flex gap-3 w-full sm:w-auto">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search submissions…"
            className="w-full sm:w-72 rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <CSVLink
            data={csvData}
            filename={`${makeFileNameSafe(
              formInfo.name || "form"
            )}_responses.csv`}
            className="px-4 py-2 bg-green-600 text-white rounded"
          >
            Export CSV
          </CSVLink>
        </div>
      </div>

      {/* TABLE */}
      <div className="w-full overflow-x-auto">
        <table className="w-full table-fixed text-sm border-collapse">
          <colgroup>
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col />
          </colgroup>

          <thead className="bg-gray-200">
            <tr>
              <th className="p-2 text-left">Actions</th>
              <th className="p-2 text-left">Member</th>
              <th className="p-2 text-left">First Name</th>
              <th className="p-2 text-left">Last Name</th>
              <th className="p-2 text-left">Email</th>
              <th className="p-2 text-left">Phone</th>
              <th className="p-2 text-left">Filled By</th>
              <th className="p-2 text-left">Submitted Date</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Total Price</th>
            </tr>
          </thead>

          <tbody>
            {filteredResponses.map((r) => {
              const isExpanded = selectedResponseId === r.responseId;

              return (
                <React.Fragment key={r.responseId}>
                  <tr className="border-t align-top">
                    <td className="p-2">
                      <div className="flex flex-col gap-1">
                        <button
                          className="text-blue-600 hover:underline text-left"
                          onClick={() => {
                            setSelectedResponseId(r.responseId);
                            setShowEditModal(true);
                          }}
                        >
                          Edit
                        </button>

                        <button
                          className="text-green-600 hover:underline text-left"
                          onClick={() => handleDownloadReport(r.responseId)}
                          disabled={downloadingReportId === r.responseId}
                        >
                          {downloadingReportId === r.responseId
                            ? "Downloading..."
                            : "Download Report"}
                        </button>

                        <button
                          className="text-red-600 hover:underline text-left"
                          onClick={() => requestDelete(r.responseId)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                    <td className="p-2">{r.memberName}</td>
                    <td className="p-2">{r.submittedByFirstName}</td>
                    <td className="p-2">{r.submittedByLastName}</td>
                    <td
                      className="p-2 text-ellipsis truncate"
                      title={r.submittedByEmail}
                    >
                      {r.submittedByEmail}
                    </td>
                    <td
                      className="p-2 text-ellipsis truncate"
                      title={r.submittedByPhone}
                    >
                      {r.submittedByPhone}
                    </td>
                    <td className="p-2 text-ellipsis truncate" title={r.filledByName || ""}>
                      {r.filledByName || "—"}
                    </td>
                    <td className="p-2 text-ellipsis truncate">
                      {new Date(r.submittedDate).toLocaleString()}
                    </td>
                    <td className="p-2">{r.status ?? "Pending"}</td>
                    <td className="p-2">
                      {r.totalPrice != null
                        ? `$${r.totalPrice.toFixed(2)}`
                        : ""}
                    </td>
                  </tr>

                  {/* Expandable Panel */}
                  <tr>
                    <td colSpan={9} className="p-2 bg-gray-50 text-right">
                      <button
                        className="text-sm text-blue-600 hover:underline w-full text-right"
                        onClick={() =>
                          setSelectedResponseId(
                            isExpanded ? null : r.responseId
                          )
                        }
                      >
                        {isExpanded ? "Hide Answers" : "Show Answers"}
                      </button>

                      {isExpanded && (
                        <div className="mt-2 space-y-2 text-left">
                          {r.answers?.map((a, idx) => {
                            const isBoothAnswer =
                              (a.question ?? "")
                                .toLowerCase()
                                .includes("booth") && !!a.answer;
                            return (
                            <div
                              key={idx}
                              className={`border rounded p-2 bg-white ${
                                isBoothAnswer
                                  ? "border-blue-400 border-2 p-4"
                                  : "border-gray-300"
                              }`}
                            >
                              <div>
                                <strong className={isBoothAnswer ? "text-base" : ""}>
                                  {a.question}
                                </strong>
                                {isBoothAnswer ? (
                                  <div className="mt-2">
                                    <p className="text-sm font-bold uppercase tracking-wide text-blue-900">
                                      Selected booth
                                    </p>
                                    <p className="text-2xl font-bold text-gray-900">
                                      {a.answer}
                                    </p>
                                  </div>
                                ) : (
                                  <>
                                    : {a.answer}
                                    {a.price ? ` ($${a.price.toFixed(2)})` : ""}
                                  </>
                                )}
                                {isBoothAnswer && a.price
                                  ? (
                                    <p className="text-sm text-gray-600 mt-1">
                                      ${a.price.toFixed(2)}
                                    </p>
                                  )
                                  : null}
                              </div>

                              {/* Display field-level users */}
                              {a.users && a.users.length > 0 && (
                                <div className="mt-2 ml-4 p-2 bg-blue-50 border border-blue-200 rounded">
                                  <p className="text-sm font-semibold mb-1">
                                    Participants:
                                  </p>
                                  {a.users.map((user, uIdx) => (
                                    <div key={uIdx} className="text-sm">
                                      {uIdx + 1}. {user.firstName}{" "}
                                      {user.lastName}
                                      {user.userType && ` - ${user.userType}`}
                                      {user.price
                                        ? ` ($${user.price.toFixed(2)})`
                                        : ""}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Display option-level users */}
                              {a.optionUsers &&
                                Object.keys(a.optionUsers).length > 0 && (
                                  <div className="mt-2 ml-4 space-y-2">
                                    {Object.entries(a.optionUsers).map(
                                      ([optionValue, users]) => (
                                        <div
                                          key={optionValue}
                                          className="p-2 bg-green-50 border border-green-200 rounded"
                                        >
                                          <p className="text-sm font-semibold mb-1">
                                            Participants for "{optionValue}":
                                          </p>
                                          {users.map((user, uIdx) => (
                                            <div key={uIdx} className="text-sm">
                                              {uIdx + 1}. {user.firstName}{" "}
                                              {user.lastName}
                                              {user.userType &&
                                                ` - ${user.userType}`}
                                              {user.price
                                                ? ` ($${user.price.toFixed(2)})`
                                                : ""}
                                            </div>
                                          ))}
                                        </div>
                                      )
                                    )}
                                  </div>
                                )}
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* EDIT MODAL */}
      {showEditModal && selectedResponseId && (
        <Modal
          show={showEditModal}
          size="4xl"
          onClose={() => setShowEditModal(false)}
          dismissible
        >
          <Modal.Header>Edit Form Submission</Modal.Header>

          <Modal.Body>
            <div className="max-h-[70vh] overflow-y-auto pr-4 pb-4">
              {selectedResponseId && (
                <EditFormResponsePage
                  responseId={selectedResponseId}
                  onClose={() => {
                    setShowEditModal(false);
                    fetchData();
                  }}
                  bindSubmit={(submitFn) => {
                    setExternalSubmitFn(() => submitFn);
                  }}
                />
              )}
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button
              className="buttonMain"
              onClick={() => {
                if (externalSubmitFn) {
                  externalSubmitFn();
                } else {
                  toast.error("Submit function not ready.");
                }
              }}
            >
              Save
            </Button>

            <Button color="gray" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {confirmDeleteId !== null && (
        <Modal
          show={confirmDeleteId !== null}
          size="md"
          onClose={() => setConfirmDeleteId(null)}
          popup
        >
          <Modal.Header />
          <Modal.Body>
            <div className="text-center p-4">
              <h3 className="mb-2 text-lg font-semibold">
                Delete this submission?
              </h3>
              {(() => {
                const rec = responses.find(
                  (x) => x.responseId === confirmDeleteId
                );
                return (
                  <p className="text-sm text-gray-700">
                    {rec ? (
                      <>
                        <span className="font-medium">Member:</span>{" "}
                        {rec.memberName}
                        <br />
                        <span className="font-medium">Submitted:</span>{" "}
                        {new Date(rec.submittedDate).toLocaleString()}
                      </>
                    ) : (
                      "This action cannot be undone."
                    )}
                  </p>
                );
              })()}
              <p className="text-xs text-gray-500 mt-2">
                This action cannot be undone.
              </p>

              <div className="mt-5 flex justify-center gap-3">
                <Button
                  color="failure"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : "Delete"}
                </Button>
                <Button
                  color="gray"
                  onClick={() => setConfirmDeleteId(null)}
                  disabled={deleting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default FormSubmissionsPage;
